import { DEFAULT_COUNTRY_PHONE_CODE, OTP_LIM_KEY, OTP_REMAINING_STATE } from '../../../constants';
import { getStorageItem, removeStorageItem, secondsToTime, setStorageItem } from 'utils';
import { getOtpLimitsPhoneKey } from './sessionPhoneStorage';

const resolveOtpLimitsPhoneKey = (phone: string): string => {
  return getOtpLimitsPhoneKey(phone) ?? phone;
};

export interface OtpRemainingAttempts {
  sms_remaining: number;
  whatsapp_remaining: number;
}

export type OtpRateLimitChannel = 'sms' | 'whatsapp';

export interface OtpRemainingState {
  phone: string;
  sms_remaining?: number;
  whatsapp_remaining?: number;
  sms_retry_after_until?: string;
  whatsapp_retry_after_until?: string;
  backend_limits?: boolean;
}

export class OtpRateLimitError extends Error {
  retryAfterUntil: string;
  channel: OtpRateLimitChannel;

  constructor(retryAfterUntil: string, channel: OtpRateLimitChannel) {
    super('OTP rate limit');
    this.retryAfterUntil = retryAfterUntil;
    this.channel = channel;
  }
}

const parseOtpRemainingValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const extractOtpRemainingFromRecord = (record: Record<string, unknown>): Partial<OtpRemainingAttempts> | null => {
  const smsFromRoot = parseOtpRemainingValue(record.sms_remaining);
  const whatsappFromRoot = parseOtpRemainingValue(record.whatsapp_remaining);
  const nestedAttempts = record.otp_send_remaining_attempts;
  let smsFromNested: number | null = null;
  let whatsappFromNested: number | null = null;

  if (nestedAttempts && typeof nestedAttempts === 'object' && !Array.isArray(nestedAttempts)) {
    const nestedRecord = nestedAttempts as Record<string, unknown>;
    smsFromNested = parseOtpRemainingValue(nestedRecord.sms_remaining);
    whatsappFromNested = parseOtpRemainingValue(nestedRecord.whatsapp_remaining);
  }

  const smsRemaining = smsFromRoot ?? smsFromNested;
  const whatsappRemaining = whatsappFromRoot ?? whatsappFromNested;

  if (smsRemaining === null && whatsappRemaining === null) {
    return null;
  }

  return {
    ...(smsRemaining !== null ? { sms_remaining: smsRemaining } : {}),
    ...(whatsappRemaining !== null ? { whatsapp_remaining: whatsappRemaining } : {}),
  };
};

const hasNestedOtpSendRemainingAttempts = (record: Record<string, unknown>): boolean => {
  const nestedAttempts = record.otp_send_remaining_attempts;

  return nestedAttempts !== null && typeof nestedAttempts === 'object' && !Array.isArray(nestedAttempts);
};

export const hasBackendOtpRemainingAttempts = (source: unknown): boolean => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return false;
  }

  const record = source as Record<string, unknown>;

  if (record.otp_backend_limits === true) {
    return true;
  }

  if (extractOtpRemainingFromRecord(record)) {
    return true;
  }

  return hasNestedOtpSendRemainingAttempts(record);
};

export const extractOtpRemainingAttempts = (source: unknown): Partial<OtpRemainingAttempts> | null => {
  if (!hasBackendOtpRemainingAttempts(source)) {
    return null;
  }

  return extractOtpRemainingFromRecord(source as Record<string, unknown>);
};

export const clearLegacyOtpLimitStorage = (): void => {
  removeStorageItem(OTP_LIM_KEY);
};

const mergeOtpRemainingFields = (target: Record<string, unknown>, partial: Partial<OtpRemainingAttempts>): void => {
  if (target.sms_remaining === undefined && partial.sms_remaining !== undefined) {
    target.sms_remaining = partial.sms_remaining;
  }

  if (target.whatsapp_remaining === undefined && partial.whatsapp_remaining !== undefined) {
    target.whatsapp_remaining = partial.whatsapp_remaining;
  }
};

export const withOtpEnvelopeFields = <T extends Record<string, unknown>>(
  statusData: T,
  axiosResponse: { data?: unknown },
): T & Partial<OtpRemainingAttempts> => {
  const result: Record<string, unknown> = { ...statusData };
  let hasBackendLimits = hasNestedOtpSendRemainingAttempts(result);

  const fromStatusData = extractOtpRemainingFromRecord(result);

  if (fromStatusData) {
    mergeOtpRemainingFields(result, fromStatusData);
    hasBackendLimits = true;
  }

  const envelope = axiosResponse?.data;

  if (envelope && typeof envelope === 'object' && !Array.isArray(envelope)) {
    const envelopeRecord = envelope as Record<string, unknown>;
    const innerData = envelopeRecord.data;

    const sources = [
      envelopeRecord,
      ...(innerData && typeof innerData === 'object' && !Array.isArray(innerData)
        ? [innerData as Record<string, unknown>]
        : []),
    ];

    sources.forEach(source => {
      if (hasNestedOtpSendRemainingAttempts(source)) {
        hasBackendLimits = true;
      }

      const partial = extractOtpRemainingFromRecord(source);

      if (partial) {
        mergeOtpRemainingFields(result, partial);
        hasBackendLimits = true;
      }
    });
  }

  if (hasBackendLimits) {
    result.otp_backend_limits = true;
  }

  return result as T & Partial<OtpRemainingAttempts>;
};

export const mergeAndSaveOtpRemainingAttempts = (
  phone: string,
  partial: Partial<OtpRemainingAttempts>,
): OtpRemainingAttempts | null => {
  clearOtpRemainingStateIfPhoneMismatch(phone);

  const smsRemaining = parseOtpRemainingValue(partial.sms_remaining);
  const whatsappRemaining = parseOtpRemainingValue(partial.whatsapp_remaining);

  if (smsRemaining === null && whatsappRemaining === null) {
    return null;
  }

  const current = getStoredOtpRemainingState(phone);
  const attempts: OtpRemainingAttempts = {
    sms_remaining: smsRemaining ?? current?.sms_remaining ?? 0,
    whatsapp_remaining: whatsappRemaining ?? current?.whatsapp_remaining ?? 0,
  };

  saveOtpRemainingAttempts(phone, attempts);

  return attempts;
};

export const normalizeOtpPhoneKey = (phone: string): string => {
  let normalized = phone;

  if (normalized.startsWith(DEFAULT_COUNTRY_PHONE_CODE)) {
    normalized = normalized.substring(DEFAULT_COUNTRY_PHONE_CODE.length);
  }

  return normalized.replace(/\s/g, '');
};

export const parseRetryAfterHeader = (header: string | undefined): string | null => {
  if (!header) {
    return null;
  }

  const parsedDate = new Date(header);

  if (!Number.isNaN(parsedDate.getTime())) {
    return header;
  }

  const retrySeconds = Number.parseInt(header, 10);

  if (!Number.isNaN(retrySeconds)) {
    return new Date(Date.now() + retrySeconds * 1000).toISOString();
  }

  return null;
};

export const getRetryAfterSeconds = (retryAfterUntil: string): number => {
  const targetMs = new Date(retryAfterUntil).getTime();

  if (Number.isNaN(targetMs)) {
    return 0;
  }

  return Math.max(0, Math.ceil((targetMs - Date.now()) / 1000));
};

export const readRawOtpRemainingState = (): OtpRemainingState | null => {
  const raw = getStorageItem(OTP_REMAINING_STATE);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as OtpRemainingState;
  } catch {
    return null;
  }
};

export const clearOtpRemainingStateIfPhoneMismatch = (phone: string): void => {
  const stored = readRawOtpRemainingState();
  const limitsPhone = resolveOtpLimitsPhoneKey(phone);

  if (!stored) {
    return;
  }

  if (normalizeOtpPhoneKey(stored.phone) !== normalizeOtpPhoneKey(limitsPhone)) {
    removeStoredOtpRemainingState();
  }
};

export const getStoredOtpRemainingState = (phone: string): OtpRemainingState | null => {
  const parsed = readRawOtpRemainingState();
  const limitsPhone = resolveOtpLimitsPhoneKey(phone);

  if (!parsed) {
    return null;
  }

  if (normalizeOtpPhoneKey(parsed.phone) !== normalizeOtpPhoneKey(limitsPhone)) {
    return null;
  }

  return parsed;
};

export interface ChannelRateLimitTimers {
  sms: number;
  whatsapp: number;
}

export interface ResolvedOtpRemaining {
  attempts: OtpRemainingAttempts | null;
  rateLimitSeconds: ChannelRateLimitTimers;
  usesBackendLimits: boolean;
}

const hasStoredAttemptCounts = (stored: OtpRemainingState): boolean => {
  return typeof stored.sms_remaining === 'number' && typeof stored.whatsapp_remaining === 'number';
};

const getChannelRateLimitSeconds = (retryAfterUntil?: string): number => {
  if (!retryAfterUntil) {
    return 0;
  }

  return getRetryAfterSeconds(retryAfterUntil);
};

const persistOtpRemainingState = (state: OtpRemainingState): void => {
  setStorageItem(OTP_REMAINING_STATE, JSON.stringify(state));
};

const buildResolvedRateLimitSeconds = (stored: OtpRemainingState): ChannelRateLimitTimers => {
  return {
    sms: getChannelRateLimitSeconds(stored.sms_retry_after_until),
    whatsapp: getChannelRateLimitSeconds(stored.whatsapp_retry_after_until),
  };
};

const clearExpiredChannelRateLimits = (phone: string, stored: OtpRemainingState): OtpRemainingState => {
  const nextState: OtpRemainingState = { ...stored, phone: normalizeOtpPhoneKey(phone) };
  let hasChanges = false;

  if (stored.sms_retry_after_until && getChannelRateLimitSeconds(stored.sms_retry_after_until) === 0) {
    delete nextState.sms_retry_after_until;
    hasChanges = true;
  }

  if (stored.whatsapp_retry_after_until && getChannelRateLimitSeconds(stored.whatsapp_retry_after_until) === 0) {
    delete nextState.whatsapp_retry_after_until;
    hasChanges = true;
  }

  if (hasChanges) {
    persistOtpRemainingState(nextState);
  }

  return hasChanges ? nextState : stored;
};

export const resolveOtpRemainingForPhone = (phone: string): ResolvedOtpRemaining => {
  clearOtpRemainingStateIfPhoneMismatch(phone);

  const stored = getStoredOtpRemainingState(phone);

  if (!stored) {
    return {
      attempts: null,
      rateLimitSeconds: { sms: 0, whatsapp: 0 },
      usesBackendLimits: false,
    };
  }

  const normalizedStored = clearExpiredChannelRateLimits(phone, stored);
  const usesBackendLimits = normalizedStored.backend_limits === true;

  return {
    attempts: hasStoredAttemptCounts(normalizedStored)
      ? {
          sms_remaining: normalizedStored.sms_remaining as number,
          whatsapp_remaining: normalizedStored.whatsapp_remaining as number,
        }
      : null,
    rateLimitSeconds: buildResolvedRateLimitSeconds(normalizedStored),
    usesBackendLimits,
  };
};

export const saveOtpRemainingAttempts = (phone: string, attempts: OtpRemainingAttempts): void => {
  clearLegacyOtpLimitStorage();

  const limitsPhone = resolveOtpLimitsPhoneKey(phone);
  const current = getStoredOtpRemainingState(limitsPhone);

  persistOtpRemainingState({
    phone: normalizeOtpPhoneKey(limitsPhone),
    sms_remaining: attempts.sms_remaining,
    whatsapp_remaining: attempts.whatsapp_remaining,
    sms_retry_after_until: current?.sms_retry_after_until,
    whatsapp_retry_after_until: current?.whatsapp_retry_after_until,
    backend_limits: true,
  });
};

export const saveOtpChannelRetryAfter = (
  phone: string,
  channel: OtpRateLimitChannel,
  retryAfterUntil: string,
): void => {
  clearOtpRemainingStateIfPhoneMismatch(phone);

  const limitsPhone = resolveOtpLimitsPhoneKey(phone);
  const current = getStoredOtpRemainingState(limitsPhone);

  persistOtpRemainingState({
    phone: normalizeOtpPhoneKey(limitsPhone),
    sms_remaining: current?.sms_remaining,
    whatsapp_remaining: current?.whatsapp_remaining,
    sms_retry_after_until: channel === 'sms' ? retryAfterUntil : current?.sms_retry_after_until,
    whatsapp_retry_after_until: channel === 'whatsapp' ? retryAfterUntil : current?.whatsapp_retry_after_until,
    backend_limits: current?.backend_limits,
  });
};

export const clearOtpChannelRateLimit = (phone: string, channel: OtpRateLimitChannel): void => {
  const stored = getStoredOtpRemainingState(phone);

  if (!stored) {
    return;
  }

  if (channel === 'sms' && !stored.sms_retry_after_until) {
    return;
  }

  if (channel === 'whatsapp' && !stored.whatsapp_retry_after_until) {
    return;
  }

  const nextState: OtpRemainingState = {
    phone: stored.phone,
    sms_remaining: stored.sms_remaining,
    whatsapp_remaining: stored.whatsapp_remaining,
    backend_limits: stored.backend_limits,
  };

  if (channel !== 'sms' && stored.sms_retry_after_until) {
    nextState.sms_retry_after_until = stored.sms_retry_after_until;
  }

  if (channel !== 'whatsapp' && stored.whatsapp_retry_after_until) {
    nextState.whatsapp_retry_after_until = stored.whatsapp_retry_after_until;
  }

  if (
    !hasStoredAttemptCounts(nextState) &&
    !nextState.sms_retry_after_until &&
    !nextState.whatsapp_retry_after_until &&
    !nextState.backend_limits
  ) {
    removeStoredOtpRemainingState();
    return;
  }

  persistOtpRemainingState(nextState);
};

export const removeStoredOtpRemainingState = (): void => {
  removeStorageItem(OTP_REMAINING_STATE);
};

export const persistOtpRemainingFromApiResponse = (phone: string, response: unknown): OtpRemainingAttempts | null => {
  const partial = extractOtpRemainingAttempts(response);

  if (!partial) {
    return null;
  }

  return mergeAndSaveOtpRemainingAttempts(resolveOtpLimitsPhoneKey(phone), partial);
};

export const resolveOtpAxiosRateLimitError = (
  error: unknown,
  phone: string,
  channel: OtpRateLimitChannel,
): OtpRateLimitError | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }

  const axiosError = error as {
    response?: {
      status?: number;
      headers?: Record<string, string | undefined>;
    };
  };

  if (axiosError.response?.status !== 429) {
    return null;
  }

  const headers = axiosError.response.headers ?? {};
  const retryAfterHeader = headers['retry-after'] ?? headers['Retry-After'];
  const retryAfterUntil = parseRetryAfterHeader(retryAfterHeader);

  if (phone && retryAfterUntil) {
    saveOtpChannelRetryAfter(phone, channel, retryAfterUntil);
  }

  return new OtpRateLimitError(retryAfterUntil ?? '', channel);
};

export const formatOtpAttemptsLabel = (remaining: number): string => {
  const pluralEnding = remaining === 1 ? '' : 's';

  return `${remaining} intento${pluralEnding} restante${pluralEnding}`;
};

export const formatOtpRateLimitLabel = (seconds: number): string => {
  return `Volver a intentar solicitar un código en ${secondsToTime(seconds)}`;
};
